import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import Axios from "axios";

import {
    Button,
    ButtonToolbar,
    Heading,
    Table,
    Text,
    Tooltip,
    Whisper,
} from "rsuite";
import { SortType } from "rsuite-table";

import HelpOutlineIcon from "@rsuite/icons/HelpOutline";

import constants from "utils/constants";
import { getModalTableHeight } from "utils/measurement-helper";
import { formatDateTimeShort } from "utils/date-helper";
import { getSorted } from "utils/sort-halper";

import { Tenant } from "store/environments/environment-types";
import { StorageAccount } from "store/storage-accounts/storage-accounts-types";
import { OperationHistory } from "store/common/operation-types";

import { loadManagementPolicyRulesHistory } from "api/environments/tenants/storage-accounts-api";
import { getErrorMessage } from "api/defaults";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";

import { setModalLoaderVisibility } from "store/common/modal-window/modal-window-slices";
import { setErrorResponseMessage } from "store/common/initialization/initialization-actions";

import ModalContainer from "components/Common/Modal/ModalContainer";

import { getAppliedRuleValue, ruleNames } from "../storage-account-helpers";
import { HistoryContext } from "../StorageAccounts";

import "./History.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    title?: string;
    open: boolean;

    setOpen?: (open: boolean) => void;
}

const History: React.FC<Props> = ({ open, setOpen }: Props) => {
    const cancelTokenSource = Axios.CancelToken.source();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const tenant = useContext(HistoryContext);

    const [historyLogs, setHistoryLogs] = useImmer<
        ReadonlyArray<OperationHistory<StorageAccount>>
    >([]);

    const [sortColumn, setSortColumn] =
        useState<keyof OperationHistory<StorageAccount>>("editionDate");
    const [sortType, setSortType] = useState<SortType>("desc");

    const title = t("storageAccounts.history");

    const tenantId = tenant?.id;

    const tableHeight = getModalTableHeight(historyLogs.length);
    const fetchData = () => {
        dispatch(setModalLoaderVisibility(true));
        dispatch(setErrorResponseMessage(""));

        Promise.all([fetchManagementPolicyRulesHistory()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    dispatch(setErrorResponseMessage(message));
                }
            })
            .finally(() => {
                dispatch(setModalLoaderVisibility(false));
            });
    };

    const fetchManagementPolicyRulesHistory = async (): Promise<
        string | null
    > => {
        try {
            const [payload, status] = await loadManagementPolicyRulesHistory(
                activeEnvironmentId,
                tenantId!,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setHistoryLogs(
                    getSorted(
                        [...payload],
                        sortColumn as keyof OperationHistory<StorageAccount>,
                        sortType
                    )
                );

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSortColumn = (sortColumn: string, sortType: any) => {
        setSortColumn(sortColumn as keyof OperationHistory<StorageAccount>);
        setSortType(sortType);

        setHistoryLogs((draft) =>
            getSorted(
                draft,
                sortColumn as keyof OperationHistory<StorageAccount>,
                sortType
            )
        );
    };

    const handleModalClose = () => {
        setSortColumn("editionDate");
        setSortType("desc");

        setOpen?.(false);
    };

    useEffect(() => {
        if (open && activeEnvironmentId) {
            fetchData();
        }
    }, [open, activeEnvironmentId]);

    const header = <Heading level={1}>{title}</Heading>;
    const footer = (
        <ButtonToolbar>
            <Button
                style={{ height: "2.5rem", width: "5rem" }}
                type="submit"
                appearance="ghost"
                onClick={handleModalClose}
            >
                {t("deliverables.packages.history.close")}
            </Button>
        </ButtonToolbar>
    );

    return (
        <ModalContainer
            className={"management-policy-rules-history__modal"}
            open={open}
            header={header}
            footer={footer}
            onClose={handleModalClose}
        >
            <Table
                shouldUpdateScroll={false}
                virtualized={true}
                data={historyLogs}
                height={tableHeight}
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
            >
                <Table.Column flexGrow={0.5} sortable={true}>
                    <Table.HeaderCell>
                        {t(`storageAccounts.errorLogsTable.modifiedBy`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey="editor">
                        {(rowData: OperationHistory<StorageAccount>) => (
                            <Text>{rowData.editor}</Text>
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={0.25} sortable={true}>
                    <Table.HeaderCell>
                        {t(`storageAccounts.errorLogsTable.modifiedOn`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey="editionDate">
                        {(rowData: OperationHistory<StorageAccount>) => (
                            <Text>
                                {formatDateTimeShort(
                                    new Date(rowData.editionDate)
                                )}
                            </Text>
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={0.2} minWidth={80}>
                    <Table.HeaderCell>
                        <Text>
                            {t(
                                `storageAccounts.storageAccountsTable.cool.name`
                            )}

                            <Whisper
                                placement="topStart"
                                trigger="hover"
                                speaker={
                                    <Tooltip>
                                        {t(
                                            `storageAccounts.storageAccountsTable.cool.helpText`
                                        )}
                                    </Tooltip>
                                }
                            >
                                <HelpOutlineIcon
                                    style={{
                                        marginLeft: "0.25rem",
                                        marginBottom: "0.125rem",
                                    }}
                                />
                            </Whisper>
                        </Text>
                    </Table.HeaderCell>
                    <Table.Cell dataKey="data.managementPolicyRules">
                        {(rowData: Tenant<StorageAccount>) => (
                            <Text>
                                {getAppliedRuleValue(
                                    rowData.data.managementPolicyRules,
                                    ruleNames.cool
                                )}
                            </Text>
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={0.2} minWidth={80}>
                    <Table.HeaderCell>
                        <Text>
                            {t(
                                `storageAccounts.storageAccountsTable.cold.name`
                            )}
                            <Whisper
                                placement="topStart"
                                trigger="hover"
                                speaker={
                                    <Tooltip>
                                        {t(
                                            `storageAccounts.storageAccountsTable.cold.helpText`
                                        )}
                                    </Tooltip>
                                }
                            >
                                <HelpOutlineIcon
                                    style={{
                                        marginLeft: "0.25rem",
                                        marginBottom: "0.125rem",
                                    }}
                                />
                            </Whisper>
                        </Text>
                    </Table.HeaderCell>
                    <Table.Cell dataKey="data.managementPolicyRules">
                        {(rowData: Tenant<StorageAccount>) => (
                            <Text>
                                {getAppliedRuleValue(
                                    rowData.data.managementPolicyRules,
                                    ruleNames.cold
                                )}
                            </Text>
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={0.2} minWidth={80}>
                    <Table.HeaderCell>
                        <Text>
                            {t(
                                `storageAccounts.storageAccountsTable.archive.name`
                            )}
                            <Whisper
                                placement="topStart"
                                trigger="hover"
                                speaker={
                                    <Tooltip>
                                        {t(
                                            `storageAccounts.storageAccountsTable.archive.helpText`
                                        )}
                                    </Tooltip>
                                }
                            >
                                <HelpOutlineIcon
                                    style={{
                                        marginLeft: "0.25rem",
                                        marginBottom: "0.125rem",
                                    }}
                                />
                            </Whisper>
                        </Text>
                    </Table.HeaderCell>
                    <Table.Cell dataKey="data.managementPolicyRules">
                        {(rowData: Tenant<StorageAccount>) => (
                            <Text>
                                {getAppliedRuleValue(
                                    rowData.data.managementPolicyRules,
                                    ruleNames.archive
                                )}
                            </Text>
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={0.2} minWidth={80}>
                    <Table.HeaderCell>
                        <Text>
                            {t(
                                `storageAccounts.storageAccountsTable.delete.name`
                            )}
                            <Whisper
                                placement="topStart"
                                trigger="hover"
                                speaker={
                                    <Tooltip>
                                        {t(
                                            `storageAccounts.storageAccountsTable.delete.helpText`
                                        )}
                                    </Tooltip>
                                }
                            >
                                <HelpOutlineIcon
                                    style={{
                                        marginLeft: "0.25rem",
                                        marginBottom: "0.125rem",
                                    }}
                                />
                            </Whisper>
                        </Text>
                    </Table.HeaderCell>
                    <Table.Cell dataKey="data.managementPolicyRules">
                        {(rowData: Tenant<StorageAccount>) => (
                            <Text>
                                {getAppliedRuleValue(
                                    rowData.data.managementPolicyRules,
                                    ruleNames.delete
                                )}
                            </Text>
                        )}
                    </Table.Cell>
                </Table.Column>
            </Table>
        </ModalContainer>
    );
};

export default History;
